import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { agentAPI, 
  AgentStats, 
  AgentOrder, 
  AgentCustomer, 
  AgentProduct,
  CreateOrderData,
  CreateCustomerData 
} from '../services/agentAPI';

interface UseAgentOptions {
  autoLoadStats?: boolean;
  autoLoadOrders?: boolean;
  timeRange?: string;
}

interface UseAgentReturn {
  // Data
  stats: AgentStats | null;
  orders: AgentOrder[];
  customers: AgentCustomer[];
  topProducts: AgentProduct[];
  
  // Loading states
  loading: boolean;
  statsLoading: boolean;
  ordersLoading: boolean;
  customersLoading: boolean;
  productsLoading: boolean;
  
  // Error states
  error: string | null;
  
  // Actions
  refreshStats: () => Promise<void>;
  refreshOrders: () => Promise<void>;
  refreshCustomers: () => Promise<void>;
  refreshTopProducts: () => Promise<void>;
  
  // Order operations
  createOrder: (data: CreateOrderData) => Promise<AgentOrder | null>;
  updateOrder: (id: string, data: Partial<AgentOrder>) => Promise<AgentOrder | null>;
  cancelOrder: (id: string, reason?: string) => Promise<boolean>;
  
  // Customer operations
  createCustomer: (data: CreateCustomerData) => Promise<AgentCustomer | null>;
  updateCustomer: (id: string, data: Partial<CreateCustomerData>) => Promise<AgentCustomer | null>;
  
  // Utility functions
  searchOrders: (query: string) => AgentOrder[];
  searchCustomers: (query: string) => AgentCustomer[];
  
  // Time range
  timeRange: string;
  setTimeRange: (range: string) => void;
}

export const useAgent = (options: UseAgentOptions = {}): UseAgentReturn => {
  const {
    autoLoadStats = true,
    autoLoadOrders = true,
    timeRange: initialTimeRange = '30d'
  } = options;

  // State
  const [stats, setStats] = useState<AgentStats | null>(null);
  const [orders, setOrders] = useState<AgentOrder[]>([]);
  const [customers, setCustomers] = useState<AgentCustomer[]>([]);
  const [topProducts, setTopProducts] = useState<AgentProduct[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState(initialTimeRange);

  // Stats operations
  const refreshStats = useCallback(async () => {
    setStatsLoading(true);
    setError(null);
    
    try {
      const response = await agentAPI.getStats(timeRange);
      setStats(response.data.data);
    } catch (err: any) {
      // Silently handle 404 errors for endpoints not yet implemented
      if (err.response?.status === 404) {
        setStats(null);
      } else {
        const message = err.response?.data?.message || 'Failed to load stats';
        setError(message);
      }
    } finally {
      setStatsLoading(false);
    }
  }, [timeRange]);

  // Orders operations
  const refreshOrders = useCallback(async () => {
    setOrdersLoading(true);
    setError(null);
    
    try {
      const response = await agentAPI.getOrders();
      setOrders(response.data.data);
    } catch (err: any) {
      // Silently handle 404 errors for endpoints not yet implemented
      if (err.response?.status === 404) {
        setOrders([]);
      } else {
        const message = err.response?.data?.message || 'Failed to load orders';
        setError(message);
      }
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  const createOrder = useCallback(async (data: CreateOrderData): Promise<AgentOrder | null> => {
    try {
      const response = await agentAPI.createOrder(data);
      const newOrder = response.data.data;
      
      setOrders(prev => [newOrder, ...prev]);
      toast.success('Order placed successfully.');
      
      // Refresh stats to reflect new order
      refreshStats();
      
      return newOrder;
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to create order';
      toast.error(message);
      return null;
    }
  }, [refreshStats]);

  const updateOrder = useCallback(async (id: string, data: Partial<AgentOrder>): Promise<AgentOrder | null> => {
    try {
      const response = await agentAPI.updateOrder(id, data);
      const updatedOrder = response.data.data;
      
      setOrders(prev => prev.map(order => 
        order.id === id ? updatedOrder : order
      ));
      
      toast.success('Order updated successfully!');
      return updatedOrder;
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to update order';
      toast.error(message);
      return null;
    }
  }, []);

  const cancelOrder = useCallback(async (id: string, reason?: string): Promise<boolean> => {
    try {
      await agentAPI.cancelOrder(id, reason);
      
      setOrders(prev => prev.map(order => 
        order.id === id ? { ...order, status: 'failed' } : order
      ));
      
      toast.success('Order cancelled successfully!');
      refreshStats();
      return true;
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to cancel order';
      toast.error(message);
      return false;
    }
  }, [refreshStats]);

  // Customer operations
  const refreshCustomers = useCallback(async () => {
    setCustomersLoading(true);
    setError(null);
    
    try {
      const response = await agentAPI.getCustomers();
      setCustomers(response.data.data);
    } catch (err: any) {
      // Silently handle 404 errors for endpoints not yet implemented
      if (err.response?.status === 404) {
        setCustomers([]);
      } else {
        const message = err.response?.data?.message || 'Failed to load customers';
        setError(message);
      }
    } finally {
      setCustomersLoading(false);
    }
  }, []);

  const createCustomer = useCallback(async (data: CreateCustomerData): Promise<AgentCustomer | null> => {
    try {
      const response = await agentAPI.createCustomer(data);
      const newCustomer = response.data.data;
      
      setCustomers(prev => [newCustomer, ...prev]);
      toast.success('Customer created successfully!');
      
      return newCustomer;
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to create customer';
      toast.error(message);
      return null;
    }
  }, []);

  const updateCustomer = useCallback(async (id: string, data: Partial<CreateCustomerData>): Promise<AgentCustomer | null> => {
    try {
      const response = await agentAPI.updateCustomer(id, data);
      const updatedCustomer = response.data.data;
      
      setCustomers(prev => prev.map(customer => 
        customer.id === id ? updatedCustomer : customer
      ));
      
      toast.success('Customer updated successfully!');
      return updatedCustomer;
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to update customer';
      toast.error(message);
      return null;
    }
  }, []);

  // Products operations
  const refreshTopProducts = useCallback(async () => {
    setProductsLoading(true);
    setError(null);
    
    try {
      const response = await agentAPI.getTopProducts(timeRange);
      setTopProducts(response.data.data);
    } catch (err: any) {
      // Silently handle 404 errors for endpoints not yet implemented
      if (err.response?.status === 404) {
        setTopProducts([]);
      } else {
        const message = err.response?.data?.message || 'Failed to load products';
        setError(message);
      }
    } finally {
      setProductsLoading(false);
    }
  }, [timeRange]);

  // Search utilities
  const searchOrders = useCallback((query: string): AgentOrder[] => {
    if (!query.trim()) return orders;
    
    const lowercaseQuery = query.toLowerCase();
    return orders.filter(order =>
      order.customer.toLowerCase().includes(lowercaseQuery) ||
      order.package.toLowerCase().includes(lowercaseQuery) ||
      order.customerPhone.includes(query) ||
      order.customerEmail.toLowerCase().includes(lowercaseQuery)
    );
  }, [orders]);

  const searchCustomers = useCallback((query: string): AgentCustomer[] => {
    if (!query.trim()) return customers;
    
    const lowercaseQuery = query.toLowerCase();
    return customers.filter(customer =>
      customer.name.toLowerCase().includes(lowercaseQuery) ||
      customer.email.toLowerCase().includes(lowercaseQuery) ||
      customer.phone.includes(query)
    );
  }, [customers]);

  // Initial data loading
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      
      const promises = [];
      if (autoLoadStats) promises.push(refreshStats());
      if (autoLoadOrders) promises.push(refreshOrders());
      if (autoLoadOrders) promises.push(refreshCustomers());
      if (autoLoadOrders) promises.push(refreshTopProducts());
      
      try {
        await Promise.all(promises);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [autoLoadStats, autoLoadOrders, refreshStats, refreshOrders, refreshCustomers, refreshTopProducts]);

  // Refresh data when time range changes
  useEffect(() => {
    if (autoLoadStats) refreshStats();
    if (autoLoadOrders) refreshTopProducts();
  }, [timeRange, autoLoadStats, autoLoadOrders, refreshStats, refreshTopProducts]);

  return {
    // Data
    stats,
    orders,
    customers,
    topProducts,
    
    // Loading states
    loading,
    statsLoading,
    ordersLoading,
    customersLoading,
    productsLoading,
    
    // Error states
    error,
    
    // Actions
    refreshStats,
    refreshOrders,
    refreshCustomers,
    refreshTopProducts,
    
    // Order operations
    createOrder,
    updateOrder,
    cancelOrder,
    
    // Customer operations
    createCustomer,
    updateCustomer,
    
    // Utility functions
    searchOrders,
    searchCustomers,
    
    // Time range
    timeRange,
    setTimeRange,
  };
};

export default useAgent;
